import { MaterialLine, MaterialSheet, RateSource } from "../../../../api/SiteDaily";
import { amountOf, perUnitOf } from "./daySheet";

// The draft state and the category grouping behind the Material-Stores sheet.
// Kept free of JSX so the arithmetic can be read (and tested) on its own.

export interface LineDraft {
  // Today's own issue (what the day-wise entry has always been)...
  qty: string;
  // ...plus what the clerk enters against earlier civil dates, keyed by work
  // date. Saved as ONE issue (qty + Σ pendingDays) with this split beside it:
  // each portion is booked on its civil date exactly.
  pendingDays: Record<string, string>;
  rate: string;
  rateSource: RateSource;
  uomId: number;
  remarks: string;
}

export interface TaskLines {
  loading: boolean;
  error: string;
  sheet: MaterialSheet | null;
  initial: Record<string, LineDraft>;
  draft: Record<string, LineDraft>;
  saving: boolean;
}

export const lineKey = (line: MaterialLine) => `${line.materialId}-${line.taskBoqId}`;

// The rate is what the clerk ENTERS for the day — no prefill from the norm or
// moving-average suggestion (user rule: the actual rate is an input, and a
// defaulted figure would be saved as if someone had checked it).
/**
 * Today's saved issue split back into what it booked on each civil day (from
 * the allocation) and the rest — so a booking made today shows on its date and
 * can be changed. qty + Σ pendingDays always equals the saved issue.
 */
const splitSavedIssue = (line: MaterialLine): { qty: string; pendingDays: Record<string, string> } => {
  const pendingDays: Record<string, string> = {};
  let onDays = 0;
  line.civilDays.forEach((day) => {
    const v = Number(day.bookedTodayQty);
    if (v > 0) {
      pendingDays[day.workDate] = day.bookedTodayQty;
      onDays += v;
    }
  });
  if (line.qtyIssuedToday === "" || onDays <= 0) return { qty: line.qtyIssuedToday, pendingDays: {} };
  const rest = Math.round((Number(line.qtyIssuedToday) - onDays) * 10000) / 10000;
  return { qty: rest > 0.00005 ? String(rest) : "", pendingDays };
};

export const draftOf = (line: MaterialLine): LineDraft => ({
  ...splitSavedIssue(line),
  rate: line.rate,
  rateSource: line.rate !== "" ? line.rateSource || "ENTERED" : "",
  uomId: line.uomId,
  remarks: line.remarks,
});

const sameDays = (a: Record<string, string>, b: Record<string, string>) => {
  const filled = (days: Record<string, string>) =>
    Object.entries(days)
      .filter(([, v]) => v !== "")
      .sort(([x], [y]) => x.localeCompare(y))
      .join("|");
  return filled(a) === filled(b);
};

export const sameDraft = (a: LineDraft | undefined, b: LineDraft | undefined) =>
  !!a &&
  !!b &&
  a.qty === b.qty &&
  sameDays(a.pendingDays, b.pendingDays) &&
  a.rate === b.rate &&
  a.rateSource === b.rateSource &&
  a.uomId === b.uomId &&
  a.remarks === b.remarks;

export const isDirty = (entry: TaskLines | undefined) =>
  !!entry?.sheet && entry.sheet.lines.some((line) => !sameDraft(entry.draft[lineKey(line)], entry.initial[lineKey(line)]));

export const emptyTaskLines = (): TaskLines => ({
  loading: false,
  error: "",
  sheet: null,
  initial: {},
  draft: {},
  saving: false,
});

// ------------------------------------------------- target and actual figures
// TARGET is the plan for the day: the norm's allotment per unit of output times
// what the task actually produced, priced at the rate FROZEN on the norm
// application. ACTUAL is what the clerk recorded. Both are '' / null when a
// side is missing — an absent rate is not a rate of zero, and a target of 0
// would read as "nothing was planned" when the truth is "nothing was priced".

/** Σ of a numeric figure over lines, treating null as 0 but an all-null set as 0. */
const sum = (values: Array<number | null>) => values.reduce<number>((acc, v) => acc + (v ?? 0), 0);

// TARGET is measured against civil done TILL DATE (material is booked after
// the civil work, so "today's output" is usually nothing).
export const targetAmountOf = (line: MaterialLine): number | null => {
  const amount = amountOf(line.targetQtyTillDate, line.normRate);
  return amount === "" ? null : Number(amount);
};

const n = (v: string) => (v !== "" && Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Issued TILL DATE with today's draft in place of today's saved issue — what
 * the till-date target is compared with. null when nothing at all is issued.
 */
export const issuedTillDateOf = (line: MaterialLine, draft: LineDraft | undefined): number | null => {
  const total = issuedTotal(draft);
  if (line.qtyIssuedTillDate === "" && total === "") return null;
  return Math.round((n(line.qtyIssuedTillDate) - n(line.qtyIssuedToday) + n(total)) * 10000) / 10000;
};

/**
 * Actual money TILL DATE (Σ qty x rate of the issues up to the date), today's
 * saved issue replaced by the live draft. null when no issue carries a rate.
 */
export const actualTillDateAmountOf = (line: MaterialLine, draft: LineDraft | undefined): number | null => {
  const today = actualAmountOf(draft);
  if (line.amountIssuedTillDate === "" && today === null) return null;
  const savedToday = amountOf(line.qtyIssuedToday, line.rate);
  return Math.round((n(line.amountIssuedTillDate) - n(savedToday) + (today ?? 0)) * 100) / 100;
};

/** Σ of what is typed against earlier civil dates; 0 when nothing. */
export const pendingDaysTotal = (draft: LineDraft | undefined): number =>
  Object.values(draft?.pendingDays ?? {}).reduce((sum, v) => sum + (v !== "" && Number.isFinite(Number(v)) ? Number(v) : 0), 0);

/**
 * The material's whole issue for the day as saved: today's qty plus what is
 * booked against earlier civil dates. '' when neither is entered.
 */
export const issuedTotal = (draft: LineDraft | undefined): string => {
  const base = draft?.qty ?? "";
  const days = pendingDaysTotal(draft);
  if (days <= 0) return base;
  const baseNum = base !== "" && Number.isFinite(Number(base)) ? Number(base) : 0;
  return String(Math.round((baseNum + days) * 10000) / 10000);
};

export const actualAmountOf = (draft: LineDraft | undefined): number | null => {
  const amount = amountOf(issuedTotal(draft), draft?.rate ?? "");
  return amount === "" ? null : Number(amount);
};

/**
 * Variance of actual against target as a percentage, or null when either side
 * is absent or the target is zero. Positive = over the plan.
 */
export const variancePct = (actual: number | null, target: number | null): number | null =>
  actual === null || target === null || target <= 0 ? null : ((actual - target) / target) * 100;

export const UNCATEGORIZED = "Uncategorized";

export interface MaterialCategoryGroup {
  key: string; // `CAT-${categoryId}`
  categoryId: number;
  categoryName: string;
  // The SAME line objects, never copies — the editor's drafts are keyed on
  // lineKey and must keep pointing at the sheet's own rows.
  lines: MaterialLine[];
  lineCount: number;
  targetAmount: number | null;
  actualAmount: number | null;
}

/**
 * The task's lines grouped by material category, in the order the sheet shows
 * them: by TARGET amount descending, uncategorised always last.
 *
 * Deliberately not by actual cost (which is how the read-only Material Cost
 * Analysis report sorts): this is an input surface, and an actual-cost order
 * would make the blocks reorder under the clerk's cursor as a quantity is
 * typed. The target is constant for the life of the loaded sheet.
 */
export const groupByCategory = (
  lines: MaterialLine[],
  draftFor: (line: MaterialLine) => LineDraft | undefined
): MaterialCategoryGroup[] => {
  const byCategory = new Map<number, MaterialCategoryGroup>();
  lines.forEach((line) => {
    const categoryId = Number(line.categoryId) || 0;
    let group = byCategory.get(categoryId);
    if (!group) {
      group = {
        key: `CAT-${categoryId}`,
        categoryId,
        categoryName: line.categoryName || UNCATEGORIZED,
        lines: [],
        lineCount: 0,
        targetAmount: null,
        actualAmount: null,
      };
      byCategory.set(categoryId, group);
    }
    group.lines.push(line);
    group.lineCount += 1;
  });

  const groups = Array.from(byCategory.values());
  groups.forEach((group) => {
    const targets = group.lines.map(targetAmountOf);
    const actuals = group.lines.map((line) => actualTillDateAmountOf(line, draftFor(line)));
    // All-absent stays null so the header shows '—' instead of ₹ 0.00.
    group.targetAmount = targets.some((v) => v !== null) ? sum(targets) : null;
    group.actualAmount = actuals.some((v) => v !== null) ? sum(actuals) : null;
  });

  return groups.sort((a, b) => {
    if (a.categoryId === 0) return 1;
    if (b.categoryId === 0) return -1;
    const diff = (b.targetAmount ?? 0) - (a.targetAmount ?? 0);
    return diff !== 0 ? diff : a.categoryName.localeCompare(b.categoryName);
  });
};

/** The task's day totals across every group, for the panel header and footer. */
/** Totals over the groups; per-unit figures divide by `producedQty` (the
 *  civil done till date on the Material-Stores sheet). */
export const sheetTotals = (groups: MaterialCategoryGroup[], producedQty: string) => {
  const targets = groups.map((g) => g.targetAmount);
  const actuals = groups.map((g) => g.actualAmount);
  const targetAmount = targets.some((v) => v !== null) ? sum(targets) : null;
  const actualAmount = actuals.some((v) => v !== null) ? sum(actuals) : null;
  return {
    targetAmount,
    actualAmount,
    targetPerUnit: targetAmount === null ? null : perUnitOf(targetAmount, producedQty),
    actualPerUnit: actualAmount === null ? null : perUnitOf(actualAmount, producedQty),
    variancePct: variancePct(actualAmount, targetAmount),
  };
};
